import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { keepPreviousData, MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import type { ReactNode } from "react";

import { meKey } from "@/features/auth";
import { ApiError } from "@/shared/api/client";
import { ConfirmProvider } from "@/shared/components/ConfirmDialog";

import { theme } from "./theme";

/** 会话过期：清掉"我是谁"，路由守卫会把用户带回登录页，登录后回到原页面。 */
function onAuthError(error: unknown) {
  if (error instanceof ApiError && error.isAuth) {
    queryClient.setQueryData(meKey, null);
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 2_000,
      refetchOnWindowFocus: true,
      // 翻页与改筛选时保留上一批数据，表格不会先空一拍再填回来
      placeholderData: keepPreviousData,
      // 4xx 是确定的错误，重试无意义；网络与 5xx 重试两次
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
    },
  },
});

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider theme={theme} defaultMode="system">
      <CssBaseline enableColorScheme />
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider maxSnack={4} autoHideDuration={3500} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <ConfirmProvider>{children}</ConfirmProvider>
        </SnackbarProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
